import { MemreButton, MemreFlexBox, MemreRTE } from 'components/core';
import { IconName } from 'components/core/MemreIcon';
import * as Color from 'core/colors';
import { TSentence } from 'types';
import { useCreateSentence } from 'hooks/useCreateSentence';
import { useDeleteSentence } from 'hooks/useDeleteSentence';
import { useMutateSentence } from 'hooks/useMutateSentence';
import { kebabCase, debounce } from 'lodash';
import Sound from './Sound';
import ImageButton from './ImageUpload/ImageButton';

interface SentencesProps {
  itemId: string;
  assignmentId: string;
  term: string;
  sentences: TSentence[];
  onSentencesChange: (sentences: TSentence[]) => void;
}

const VocabularySentences: React.FC<SentencesProps> = (props) => {
  const { itemId, assignmentId, term, sentences, onSentencesChange } = props;
  const [createSentence] = useCreateSentence(itemId, assignmentId);
  const [deleteSentence] = useDeleteSentence(itemId, assignmentId);
  const [mutateSentence] = useMutateSentence(itemId, assignmentId);

  const handleAddSentence = async () => {
    const newSentence = await createSentence(itemId);
    if (!newSentence) return;

    onSentencesChange([...sentences, newSentence]);
  };

  const handleDeleteSentence = async (sentenceId: string) => {
    await deleteSentence(sentenceId);
    onSentencesChange(sentences.filter((s) => s.id !== sentenceId));
  };

  const autoHighlightTerm = (text: string): string => {
    if (!text || !term) {
      return text;
    }

    // Don't apply highlighting if the text already contains bold tags
    if (text.includes('<b>')) {
      return text;
    }

    // Only replace the first occurrence. The backend does not support multiple highlights
    return text.replace(new RegExp(term, 'i'), '<b>$&</b>');
  };

  const updateSentence = async (sentenceId: string, updates: Partial<TSentence>) => {
    try {
      // Check if updates include image or sound
      const hasImage = updates.hasOwnProperty('image');
      const hasSound = updates.hasOwnProperty('sound');

      const originalUpdates = { ...updates };

      // Prepare relationships object if needed
      const relationships: any = {};
      if (hasImage) {
        relationships.image = {
          data: updates.image ? { id: updates.image.id } : null,
        };
        delete updates.image;
      }

      if (hasSound) {
        relationships.sound = {
          data: updates.sound ? { id: updates.sound.id } : null,
        };
        delete updates.sound;
      }

      // Convert property names from camelCase to kebab-case for API
      const apiAttributes = Object.fromEntries(
        Object.entries(updates).map(([key, value]) => {
          // Apply auto-highlighting for textHtml field
          if (key === 'textHtml' && typeof value === 'string') {
            return [kebabCase(key), autoHighlightTerm(value)];
          }
          return [kebabCase(key), value];
        })
      );

      // Prepare payload with attributes and relationships if any
      const payload: any = {
        data: {
          attributes: apiAttributes,
        },
      };

      // Add relationships to payload if needed
      if (Object.keys(relationships).length > 0) {
        payload.data.relationships = relationships;
      }

      await mutateSentence({
        id: sentenceId,
        payload,
      });

      onSentencesChange(sentences.map((s) => (s.id === sentenceId ? { ...s, ...originalUpdates } : s)));
    } catch (error) {
      console.error('Failed to update sentence:', error);
    }
  };

  return (
    <MemreFlexBox direction="column" gap={2} sx={{ backgroundColor: Color.veryLightGray, p: 2, borderRadius: 2 }}>
      <h3>Sentences</h3>
      {sentences.length > 0 && (
        <p>Note: The first occurrence of the foreign term will be blanked out during quizzing.</p>
      )}

      {sentences.map((sentence, index) => (
        <SentenceField
          key={sentence.id}
          sentence={sentence}
          term={term}
          sentenceNumber={index + 1}
          onUpdate={(updates) => updateSentence(sentence.id, updates)}
          onDelete={() => handleDeleteSentence(sentence.id)}
        />
      ))}

      <MemreButton
        variant="text"
        text="Add Sentence"
        size="small"
        iconName={IconName.plus}
        onClick={handleAddSentence}
      />
    </MemreFlexBox>
  );
};

interface SentenceFieldProps {
  sentence: TSentence;
  term: string;
  sentenceNumber: number;
  onUpdate: (updates: Partial<TSentence>) => void;
  onDelete: () => void;
}

const SentenceField = ({ sentence, term, sentenceNumber, onUpdate, onDelete }: SentenceFieldProps) => {
  const isValid = (text: string) => {
    if (!text || !term) return true;
    // Strip HTML tags for validation check
    const textWithoutTags = text.replace(/<[^>]*>?/gm, '');
    return textWithoutTags.toLowerCase().includes(term.toLowerCase());
  };

  // Debounced handlers for RTE updates
  const debouncedTextChange = debounce((content: string) => {
    onUpdate({ textHtml: content }); // The parent component will apply autoHighlightTerm
  }, 500);

  const debouncedTranslationChange = debounce((content: string) => {
    onUpdate({ translationHtml: content });
  }, 500);

  // Image handling
  const handleAddImage = async (imageUrl: string, imageId: string) => {
    onUpdate({ image: { id: imageId, url: imageUrl } });
  };

  const handleDeleteImage = async () => {
    onUpdate({ image: null });
  };

  // Sound handling
  const handleAddSound = async (soundUrl: string, soundId: string) => {
    onUpdate({ sound: { id: soundId, url: soundUrl } });
  };

  const handleDeleteSound = async () => {
    onUpdate({ sound: null });
  };

  return (
    <MemreFlexBox
      direction="column"
      gap={2}
      sx={{
        backgroundColor: 'white',
        p: 2,
        borderRadius: 2,
        border: `1px solid ${Color.lightGray}`,
      }}
    >
      <MemreFlexBox justify="space-between" align="center" sx={{ width: '100%' }}>
        <h4 style={{ margin: 0 }}>Sentence {sentenceNumber}</h4>
        <MemreButton
          variant="icon"
          iconName={IconName.trash}
          size="medium"
          onClick={onDelete}
          aria-label={`Delete sentence ${sentenceNumber}`}
          sx={{
            color: Color.darkGreen,
            '&:hover': { color: Color.regularRed },
            '& svg': { fontSize: '1.25rem' },
          }}
        />
      </MemreFlexBox>

      {/* Foreign Sentence */}
      <MemreFlexBox direction="column" gap={1} sx={{ width: '100%' }}>
        <label>Sentence in Foreign Language</label>
        <div
          style={{
            border: `1px solid ${Color.lightGray}`,
            borderRadius: '4px',
            backgroundColor: 'white',
          }}
        >
          <MemreRTE
            id={`sentence-${sentence.id}-text`}
            initialContent={sentence.textHtml || ''}
            onUpdate={debouncedTextChange}
            placeholder="Type the sentence in foreign language..."
          />
        </div>
        {!isValid(sentence.textHtml || '') && (
          <p style={{ color: 'red', margin: '4px 0' }}>Please include the foreign term in the sentence.</p>
        )}
      </MemreFlexBox>

      {/* Native Sentence */}
      <MemreFlexBox direction="column" gap={1} sx={{ width: '100%' }}>
        <label>Sentence in Native Language</label>
        <div
          style={{
            border: `1px solid ${Color.lightGray}`,
            borderRadius: '4px',
            backgroundColor: 'white',
          }}
        >
          <MemreRTE
            id={`sentence-${sentence.id}-translation`}
            initialContent={sentence.translationHtml || ''}
            onUpdate={debouncedTranslationChange}
            placeholder="Type the sentence in native language..."
          />
        </div>
      </MemreFlexBox>

      {/* Media Section */}
      <MemreFlexBox direction="row" gap={2} sx={{ mt: 1 }}>
        <ImageButton
          imageUrl={sentence.image?.url}
          onAddImage={handleAddImage}
          onDeleteImage={handleDeleteImage}
          buttonSize="small"
          imageHeight={48}
        />
        <Sound url={sentence.sound?.url} add={handleAddSound} handleDelete={handleDeleteSound} />
      </MemreFlexBox>
    </MemreFlexBox>
  );
};

export default VocabularySentences;
